/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectorRef, Component, ElementRef, Inject, KeyValueDiffers, OnChanges, ViewChild } from '@angular/core';
import { APP_BASE_HREF, DatePipe, DecimalPipe } from '@angular/common';
import * as am4core from '@amcharts/amcharts4/core';
import * as htmlToImage from 'html-to-image';
import * as papaparse from 'papaparse';

import { ConfigService } from '../../../../Core/Config/services/config.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { DataConnectionService } from '../../../../Core/Users/services/data-connection.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { SocialChartDetailsDialogService } from '../../../../Social/Intelligence/services/social-chart-details-dialog.service';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';

import { AbstractPageBuilderService } from '../../../../Common/PageBuilder/services/abstract-page-builder.service';
import { ChartBlockComponent } from '../ChartBlock/chart-block.component';
import { ClickOutsideService } from '../../../../Common/PageBuilder/services/click-outside.service';
import { CoverageMetricChartComponent } from '../../../../Common/Charts/components/CoverageMetricChart/coverage-metric-chart.component';
import { CoverageMetricChartDisplayElement } from '../../../../Common/Reports/models/coverage-metric-chart-display-element.model';
import { CoverageMetricChartDisplaySettings } from '../../../../Common/Charts/models/coverage-metric-chart-display-settings.model';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { ReportsChartDetailService } from '../../../../Common/Reports/services/reports-chart-detail.service';
import { ReportsChartOptionsService } from '../../../../Common/Reports/services/reports-chart-options.service';
import { ReportsChartSeriesConverterService } from '../../../../Common/Reports/services/reports-chart-series-converter.service';
import { ReportSeriesModel } from '../../../../Common/Reports/models/report-series.model';
import { ReportsSeriesPreviewNewService } from '../../../../Common/Reports/services/reports-series-preview-new.service';
import { ReportsSeriesPreviewService } from '../../../../Common/Reports/services/report-series-preview.service';
import { ChartExportEventModel } from '@Common/Charts/models/chart-export-event.model';
import { ChartDisplayElementModel } from '@Common/Reports/models/chart-display-element.model';
import { download } from '@Common/Misc/helpers/download/download.helper';
import { printHelper } from '@Common/Misc/helpers/print/print.helper';
import { chartColors } from '@Common/Charts/enums/chart-colors.enum';
import { ReportsPageBuilderService } from '@Common/Reports/services/reports-page-builder.service';
import { DataListChartOptionsModel } from '@Common/Charts/models/data-list-chart-options.model';


@Component({
    selector: 'agpr-coverage-metric-chart-block',
    templateUrl: './coverage-metric-chart-block.component.html',
    styleUrls: ['./coverage-metric-chart-block.component.scss'],
    standalone: false
})
export class CoverageMetricChartBlockComponent extends ChartBlockComponent implements OnChanges {

    override displayElement: CoverageMetricChartDisplayElement;

    oldCurrentValue: number;
    oldPreviousValue: number;
    series: ReportSeriesModel;
    isExporting: boolean = false;
    protected exportClass: string = 'agpr-coverageMetricChart--exporting';
    hasPreviousPeriod: boolean = false;
    declare chartOptions: DataListChartOptionsModel;

    @ViewChild('chart') chartComponent: CoverageMetricChartComponent;
    @ViewChild('exportMenu') exportMenu;
    @ViewChild('resizeIcon') resizeIcon;


    constructor(
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        public reportSeriesPreviewService: ReportsSeriesPreviewService,
        public reportsChartOptionsService: ReportsChartOptionsService,
        public localizationService: LocalizationService,
        public changeDetectorRef: ChangeDetectorRef,
        public userStateService: CurrentUserStateService,
        public monitorConfigService: MonitorConfigService,
        public userPreferencesService: UserPreferencesService,
        public clickOutsideService: ClickOutsideService,
        public reportsChartDetailService: ReportsChartDetailService,
        public socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public configService: ConfigService,
        public elementRef: ElementRef,
        public dataConnectionService: DataConnectionService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public reportsSeriesPreviewNewService: ReportsSeriesPreviewNewService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public decimalPipe: DecimalPipe,
        public datePipe: DatePipe,

    ) {
        super(
            differs,
            pageBuilderService,
            reportSeriesPreviewService,
            reportsChartOptionsService,
            localizationService,
            appBaseHref,
            changeDetectorRef,
            userStateService,
            null,
            monitorConfigService,
            userPreferencesService,
            clickOutsideService,
            reportsChartDetailService,
            socialChartDetailsDialogService,
            configService,
            elementRef,
            dataConnectionService,
            reportsChartSeriesConverterService,
            reportsSeriesPreviewNewService
        );
    }

    ngOnChanges(): void {
        this.chartOptions = this.reportsChartOptionsService.getDataListChartOptions();
        this.displayElement = this.block.displayElement as CoverageMetricChartDisplayElement;
        const chartHeaderSettings = this.displayElement.chartHeaderSettings;
        this.displayElement.styles.chartHeaderSettings = chartHeaderSettings;
        this.displayElement.styles.chartDisplaySettings = chartHeaderSettings;
        this.displayElement.series.forEach(x => x.hasPreviousPeriod = this.displayElement.chartHeaderSettings.previousPeriod);
        this.hasPreviousPeriod = this.displayElement.chartHeaderSettings?.previousPeriod;
        this.renderable = this.block.displayElement.canRender();
        if (this.renderable) {
            if ((this.pageBuilderService as ReportsPageBuilderService).mapBlockToChartComponent) {
            // If it's injected as ReportsPageBuilderService it causes a circular dependency, casting works though
                (this.pageBuilderService as ReportsPageBuilderService).mapBlockToChartComponent(this.block, this);
            }
        }


        if (!this.displayElement || !this.displayElement.series || this.displayElement.series.length < 1) {
            this.series = null;
            return;
        }

        const series = this.displayElement.series.filter(seriesItem => seriesItem.isValid());
        if (series.length < 1) {
            this.series = null;
            return;
        }

        series.forEach(_series => _series.hasPreviousPeriod =  this.hasPreviousPeriod);

        if (this.preview) {
            this.series = this.reportsSeriesPreviewNewService.getPreviewSeries(this.displayElement.series, this.block)[0];
        }   else {
            this.series = series[0];
        }

        if (this.renderable) {
            this.chartComponent?.updateChart();
        }

        const currentValue = this.displayElement.customCurrentValue;
        const previousValue = (this.displayElement?.chartHeaderSettings as CoverageMetricChartDisplaySettings)?.customPreviousValue;
        if (currentValue !== this.oldCurrentValue || previousValue !== this.oldPreviousValue) {
            this.oldCurrentValue = currentValue;
            this.oldPreviousValue = previousValue;
            this.chartComponent?.watchData();
        }
    }

    /**
     * Gets the series label from the chart
     */
    public getExportSeriesLabel(): string {
        if (this.series && this.series[0]) {
            return this.series[0].label || '';
        } else {
            return '';
        }
    }

    /**
     * Handles the export button click action, exporting the chart in the selected format
    */
    public async chartExport(event: ChartExportEventModel) {
        this.isExporting = true;
        // this removes the :hover rule that causes the border of the chart to be blue
        const container: HTMLDivElement = this.elementRef.nativeElement.children[0];
        container.classList.remove('agpr-block');

        this.exportMenu.nativeElement.style.display = 'none';

        const exportType = event.eventKey.toLowerCase() as keyof am4core.IExportOptions;

        let url: string;
        let blob: Blob;

        const displayModel = this.block.displayElement as ChartDisplayElementModel;
        requestAnimationFrame(async () => {
            switch (exportType) {
                case 'jpg':
                    url = await htmlToImage.toJpeg(this.elementRef.nativeElement.children[0]);
                    break;

                case 'png':
                    url = await htmlToImage.toPng(this.elementRef.nativeElement.children[0]);
                    break;
                case 'svg':
                    url = await htmlToImage.toSvg(this.elementRef.nativeElement.children[0]);
                    break;
                case 'csv':
                    const csvLabels = ['Metric Name', 'Date Range', 'Metric Value'];
                    const csvValues = [
                        this.localizationService.transform(`Reports.formField.analysis.${this.displayElement.metric}`),
                        this.chartComponent.dateRangeLabel,
                        this.chartComponent.currentValue.toFixed(1)
                    ];
                    const csvData = [
                        csvLabels,
                        csvValues
                    ];
                    if (this.hasPreviousPeriod && this.chartComponent.previousValue) {
                        csvLabels.push('Metric Previous Period Value');
                        csvValues.push(this.chartComponent.previousValue.toFixed(1));
                    }

                    const csvString: string = papaparse.unparse(csvData, {delimiter: ','});
                    blob = new Blob(['\ufeff', csvString], { type: 'text/csv;charset=utf-8;'});
                    break;

                case 'json':
                    const jsonData: Record<string, string | number> = {
                        'Metric Name': this.localizationService.transform(
                            `Reports.formField.analysis.${this.displayElement.metric}`
                        ),
                        'Date Range': this.chartComponent.dateRangeLabel,
                        'Metric Value': Number(this.chartComponent.currentValue.toFixed(1))
                    };

                    if (this.hasPreviousPeriod && this.chartComponent.previousValue) {
                        jsonData['Metric Previous Period Value'] = Number(this.chartComponent.previousValue.toFixed(1));
                    }
                    blob = new Blob([JSON.stringify(jsonData)], { type: 'text/json;charset=utf-8;'});
                    break;


                case 'print':
                    const printElement = this.elementRef.nativeElement.children[0];
                    const printData = await htmlToImage.toSvg(printElement);
                    printHelper.image(printData, document.title, {fit: printElement.offsetHeight <= 1500});
                    break;
            }

            if (blob) {
                download.blob(blob, `${this.displayElement.title}.${exportType}`);

            }

            if (url) {
                download.url(url, `${this.displayElement.title}.${exportType}`);
            }

            this.exportMenu.nativeElement.style.display = 'block';
            container.classList.add('agpr-block');
            this.isExporting = false;
        });

    }

    /**
     * Gets the export image for the full report pdf export
     */
    public async getFullExportImage(width: number, height: number): Promise<string> {
        const el: HTMLDivElement = this.elementRef.nativeElement.children[0];
        const classes: DOMTokenList = this.elementRef.nativeElement.children[0].classList;
        const sparkChart = el.querySelectorAll('.agpr-reports-chart')[0] as HTMLElement;

        el.style.border = 'none';

        // Sets a bunch of styles for scaling to the page size of the pdf
        classes.add(this.exportClass);
        el.style.width = width + 'px';
        el.style.minWidth = width + 'px';
        el.style.height = height + 'px';
        el.style.minHeight = height + 'px';
        el.style.flex = 'unset';
        if (sparkChart) {
            sparkChart.style.width = (width - 55) + 'px';
            sparkChart.style.height = '80px';
            sparkChart.style.paddingBottom = '30px';
        }

        await this.chartComponent.chart.chartValidated();
        const png = await htmlToImage.toPng(this.elementRef.nativeElement.children[0]);

        // Resets the styles to the original
        el.style.width = '';
        el.style.minWidth = '';
        el.style.height = '';
        el.style.minHeight = '';
        el.style.flex = '';
        if (sparkChart) {
            sparkChart.style.width = '100%';
            sparkChart.style.height = '100%';
            sparkChart.style.paddingBottom = '';
        }

        await this.chartComponent.chart.chartValidated();

        el.style.border = `1px solid ${chartColors.agprLightGrey}`;
        classes.remove(this.exportClass);

        return png;
    }

}
